import z from 'zod';
import { BaseConfigBuilder } from '@equinor/fusion-framework-module';
import semver from 'semver';
import { TelemetryLevel, } from '@equinor/fusion-framework-module-telemetry';
import { MsalClient } from './MsalClient';
import { createClientLogCallback } from './create-client-log-callback';
import { CacheLookupPolicy, LogLevel } from '@azure/msal-browser';
import { version } from './version';
/**
 * Zod schema for telemetry configuration validation.
 *
 * @internal
 */
const TelemetryConfigSchema = z.object({
    provider: z.custom().optional(),
    metadata: z.record(z.string(), z.unknown()).optional().default({
        module: 'msal',
        version,
    }),
    scope: z.array(z.string()).optional().default(['framework', 'authentication']),
});
/**
 * Zod schema for MSAL module configuration validation.
 *
 * @internal
 */
const MsalConfigSchema = z.object({
    client: z.custom().optional(),
    provider: z.custom().optional(),
    requiresAuth: z.boolean().optional(),
    redirectUri: z.string().optional(),
    loginHint: z.string().optional(),
    authCode: z.string().optional(),
    cacheLookupPolicy: z
        .custom((val) => typeof val === 'number' &&
        Object.values(CacheLookupPolicy).includes(val))
        .optional(),
    version: z.string().transform((x) => String(semver.coerce(x))),
    telemetry: TelemetryConfigSchema,
});
/**
 * Configuration builder for MSAL v4 authentication module.
 *
 * This configurator provides a fluent API for setting up Microsoft Authentication Library
 * configuration with support for MSAL v4 features and breaking changes.
 *
 * @example
 * ```typescript
 * const configurator = new MsalConfigurator();
 * configurator.setClient(new AuthClient('your-tenant-id', 'your-client-id'));
 * configurator.setRequiresAuth(true);
 * ```
 */
export class MsalConfigurator extends BaseConfigBuilder {
    #msalConfig;
    /**
     * The MSAL module version being configured.
     *
     * @default Latest
     * @returns The configured MSAL module version.
     */
    get version() {
        return version;
    }
    /**
     * Creates a new MSAL configurator instance.
     *
     * Sets up default configuration including version and telemetry provider integration.
     */
    constructor() {
        super();
        // Set default version
        this._set('version', async () => this.version);
        // Auto-detect and integrate telemetry module if available
        this._set('telemetry.provider', async (args) => {
            // Only resolve the telemetry instance when the telemetry module is registered
            if (args.hasModule('telemetry')) {
                const telemetry = await args.requireInstance('telemetry');
                return telemetry;
            }
        });
        // Default cache lookup policy to AccessTokenAndRefreshToken to avoid iframe fallback delays
        this._set('cacheLookupPolicy', async () => CacheLookupPolicy.AccessTokenAndRefreshToken);
    }
    /**
     * Sets the MSAL client configuration for authentication.
     *
     * This method stores the configuration which will be used to create an MSAL client
     * instance during module initialization. The client will be auto-created if not provided
     * via `setClient`.
     *
     * @param config - Client configuration object with MSAL settings (client ID, tenant ID, etc.)
     * @returns The configurator instance for method chaining
     *
     * @example
     * ```typescript
     * configurator.setClientConfig({
     *   auth: {
     *     clientId: 'your-client-id',
     *     tenantId: 'your-tenant-id'
     *   }
     * });
     * ```
     */
    setClientConfig(config) {
        this.#msalConfig = config;
        return this;
    }
    /**
     * Sets the cache lookup policy used for every silent token acquisition.
     *
     * Controls whether MSAL falls back to a hidden iframe when the refresh token
     * fails. Defaults to `CacheLookupPolicy.AccessTokenAndRefreshToken`, which skips
     * the iframe step and fails immediately with `InteractionRequiredAuthError` when
     * the refresh token is revoked — avoiding the ~10–20 s `monitor_window_timeout`
     * delay caused by MSAL's built-in iframe fallback.
     *
     * Set to `CacheLookupPolicy.Default` to restore MSAL's full waterfall:
     * cache → refresh token → iframe.
     *
     * @param policy - Cache lookup policy to apply
     * @returns The configurator instance for method chaining
     *
     * @example
     * ```typescript
     * import { CacheLookupPolicy } from '@azure/msal-browser';
     *
     * // Restore MSAL's built-in iframe fallback (not recommended for most apps)
     * configurator.setCacheLookupPolicy(CacheLookupPolicy.Default);
     * ```
     */
    setCacheLookupPolicy(policy) {
        this._set('cacheLookupPolicy', async () => policy);
        return this;
    }
    /**
     * Sets a backend-issued authorization code for token exchange.
     *
     * This enables the MSAL module to exchange a backend-generated auth code for tokens
     * during initialization, allowing users to be automatically signed in without triggering
     * an interactive MSAL login flow. The auth code is exchanged before the requiresAuth check,
     * so tokens are cached and no login prompt appears.
     *
     * This follows Microsoft's standard SPA Auth Code Flow pattern and is compatible with
     * MSAL Browser's acquireTokenByCode() method.
     *
     * @param authCode - The authorization code issued by the backend, or undefined to clear/reset it
     * @returns The configurator instance for method chaining
     *
     * @example
     * ```typescript
     * // Backend provides auth code in HTML/config
     * const config = { auth: { code: getAuthCodeFromBackend() } };
     * configurator.setAuthCode(config.auth.code);
     *
     * // Clear previously configured auth code
     * configurator.setAuthCode(undefined);
     * ```
     *
     * @remarks
     * - Auth codes are single-use and short-lived (typically 5-10 minutes)
     * - The exchange happens during module initialization before requiresAuth check
     * - If exchange fails, the provider falls back to standard MSAL authentication flows
     * - Passing undefined, empty, or whitespace-only values clears the configured auth code
     * - Requires backend to be configured with SPA Auth Code support
     */
    setAuthCode(authCode) {
        const normalizedAuthCode = authCode?.trim() || undefined;
        this._set('authCode', async () => normalizedAuthCode);
        return this;
    }
    /**
     * Sets whether authentication is required for the application.
     *
     * When set to true, the application will attempt automatic login during initialization
     * if no valid authentication session exists. When false, authentication is optional
     * and must be triggered manually.
     *
     * @param requiresAuth - Whether authentication is mandatory for the application
     * @returns The configurator instance for method chaining
     *
     * @example
     * ```typescript
     * // Require authentication on app load
     * configurator.setRequiresAuth(true);
     *
     * // Make authentication optional
     * configurator.setRequiresAuth(false);
     * ```
     */
    setRequiresAuth(requiresAuth) {
        this._set('requiresAuth', async () => requiresAuth);
        return this;
    }
    /**
     * Sets a default login hint for authentication flows.
     *
     * The login hint is used to pre-fill the username during authentication and
     * enables silent SSO when no account is available.
     *
     * @param loginHint - The preferred username/email to use as login hint
     * @returns The configurator instance for method chaining
     *
     * @example
     * ```typescript
     * configurator.setLoginHint('user@company.com');
     * ```
     */
    setLoginHint(loginHint) {
        this._set('loginHint', async () => loginHint);
        return this;
    }
    /**
     * Sets a pre-configured MSAL provider instance directly.
     *
     * @deprecated Since version 5.1.0. Use {@link MsalConfigurator.setClient | setClient} instead.
     *
     * @param provider - Pre-configured provider instance, or undefined to clear
     * @returns The configurator instance for method chaining
     */
    setProvider(provider) {
        this._set('provider', async () => provider);
        return this;
    }
    /**
     * Sets a pre-configured MSAL client instance.
     *
     * This method allows you to provide an already-instantiated MSAL client rather than
     * letting the configurator create one from configuration. Useful when you need
     * custom client configuration outside the standard configurator options.
     *
     * @param client - Pre-configured MSAL client instance
     * @returns The configurator instance for method chaining
     *
     * @example
     * ```typescript
     * const customClient = new MsalClient(customConfig);
     * configurator.setClient(customClient);
     * ```
     */
    setClient(client) {
        this._set('client', async () => client);
        return this;
    }
    /**
     * Sets telemetry provider for MSAL authentication events.
     *
     * This allows MSAL authentication events to be tracked through the framework's
     * telemetry system. If not provided, telemetry module will be auto-detected if
     * available in the framework configuration.
     *
     * @param telemetry - Telemetry provider instance or undefined to disable telemetry
     * @returns The configurator instance for method chaining
     */
    setTelemetry(telemetry) {
        this._set('telemetry.provider', async () => telemetry);
        return this;
    }
    /**
     * Sets optional metadata to be included on all MSAL telemetry events.
     *
     * @deprecated Use {@link MsalConfigurator.setTelemetry | setTelemetry} instead.
     *
     * @param metadata - Key-value metadata to attach to telemetry events, or undefined to clear
     * @returns The configurator instance for method chaining
     */
    setTelemetryMetadata(metadata) {
        this._set('telemetry.metadata', async () => metadata);
        return this;
    }
    /**
     * Sets the telemetry scope for MSAL authentication events.
     *
     * The scope is used to categorize and filter telemetry events in the telemetry system.
     * Default scope is ['framework', 'authentication'].
     *
     * @param scope - Array of scope identifiers for telemetry categorization
     * @returns The configurator instance for method chaining
     *
     * @example
     * ```typescript
     * configurator.setTelemetryScope(['custom', 'auth', 'msal']);
     * ```
     */
    setTelemetryScope(scope) {
        this._set('telemetry.scope', async () => scope);
        return this;
    }
    /**
     * Processes and validates the configuration.
     *
     * @param config - Raw configuration object
     * @returns Processed and validated configuration
     */
    async _processConfig(rawConfig) {
        // Validate and coerce configuration using Zod schema
        const config = await MsalConfigSchema.parseAsync(rawConfig);
        // Auto-create client if config provided but no client instance
        // This allows users to provide configuration without manually instantiating the client
        if (!config.client && this.#msalConfig) {
            const clientConfig = this.#msalConfig;
            config.telemetry.provider?.trackEvent({
                name: 'module-msal.configurator._processConfig.creating-client',
                level: TelemetryLevel.Debug,
                scope: config.telemetry.scope,
                metadata: { ...config.telemetry.metadata, clientConfig },
            });
            // Auto-generate authority URL from tenant ID if not explicitly provided
            // This simplifies configuration for most common cases
            if (!clientConfig.auth.authority && clientConfig.auth.tenantId) {
                clientConfig.auth.authority = `https://login.microsoftonline.com/${clientConfig.auth.tenantId}`;
            }
            // Set default cache location to localStorage for browser environments
            // MSAL supports sessionStorage as well, but localStorage is the standard for persistent auth
            if (!clientConfig.cache) {
                clientConfig.cache = { cacheLocation: 'localStorage' };
            }
            // Integrate framework telemetry with MSAL logging system
            // This allows MSAL events to flow through the framework's telemetry pipeline
            if (!clientConfig.system?.loggerOptions && config.telemetry?.provider) {
                const { provider, metadata, scope } = config.telemetry;
                provider.trackEvent({
                    name: 'module-msal.configurator._processConfig.client-telemetry-connected',
                    level: TelemetryLevel.Debug,
                    scope,
                    metadata,
                });
                clientConfig.system = {
                    ...clientConfig.system,
                    loggerOptions: {
                        // Only log PII in development to protect user privacy in production
                        piiLoggingEnabled: process.env.NODE_ENV === 'development',
                        // Bridge MSAL log events to framework telemetry system
                        loggerCallback: createClientLogCallback(provider, metadata, [...scope, '3rd-party']),
                        // Use Warning level by default - captures errors and warnings without being verbose
                        logLevel: LogLevel.Warning,
                        // Preserve any user-provided logger options (allows customization)
                        ...clientConfig.system?.loggerOptions,
                    },
                };
            }
            // Apply silent cache lookup policy if configured
            if (config.cacheLookupPolicy !== undefined) {
                clientConfig.cacheLookupPolicy = config.cacheLookupPolicy;
            }
            // Instantiate MSAL client with fully configured options
            config.client = new MsalClient(clientConfig);
        }
        return config;
    }
}
//# sourceMappingURL=MsalConfigurator.js.map